import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { keys, isEmpty, values, noop, isNil, clone } from 'lodash';
// Import Types
import type { DynamicVariableChannelData } from 'types';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectPageValidation } from 'store/slices/pageValidation';
import { selectPartnerSettings } from 'store/slices/partnerSettings';
import { selectPartnerLanguages } from 'store/slices/partnerLanguages';
import {
    deleteTemplateLanguage,
    selectTemplateState,
    setTemplateAsDefault,
    setTemplateValue,
} from 'store/slices/template';
// Import UI Components
import { Tab, Tabs, BusyLoader, Button } from '@geneui/components';
// Import Components
import CommonTemplateContent from './CommonTemplateContent';
import LanguageWithSearchModal from 'components/common/Modals/LanguageWithSearchModal';
import SetDefaultLanguageModal from 'components/common/Modals/SetDefaultLanguageModal';
// Import Hooks
import { useValidator } from 'hooks';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useHasPermission from 'hooks/UseHasPermission';
// Import Helpers
import { getPartnerLanguageList, ignoreCanceledError } from 'services/Helpers';
// Import Services
import { UtilsHttpService, TemplateHttpService } from 'services/http';
import {
    createLanguageModalData,
    replaceOldPlaceholders,
    getTemplateValidationTranslatableErrorText,
    groupDynamicVariablesByDisplayName,
    replaceAllLanguagesTopImageUrlPlaceholders,
} from 'services/template';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { l, TemplateTypesLabels, TemplateInputTypes } from 'constants/common';
import { IS_WHATSAPP_CASCADE_ENABLED } from 'constants/template';

const isAllowWhatsAppSmsCascading = IS_WHATSAPP_CASCADE_ENABLED;

const CommonTemplate = (props) => {
    const {
        isLoading,
        templateType,
        addValidationProperties,
        removeValidationProperties,
        maxSMSPartsCount,
        getAssets,
        addAsset,
        setTabActiveKey,
        tabActiveKey,
        withDynamicValues,
        eventId,
        shortenerAndTrackerSettings,
        templateDynamicVariables,
        setTemplateDynamicVariables,
    } = props;
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();
    const dispatch = useAppDispatch();

    const { isLoadingPartnerSettings, hasSubscriptionPlaceholder } = useHasPermission();

    const templateData = useAppSelector(selectTemplateState);
    const partnerLanguages = useAppSelector(selectPartnerLanguages);
    const pageValidation = useAppSelector(selectPageValidation);
    const partnerSettings = useAppSelector(selectPartnerSettings);
    let isAllowViberSmsCascading;

    if (!isEmpty(partnerSettings)) {
        isAllowViberSmsCascading = !!partnerSettings.IsAllowViberSmsCascading;
    }

    const validator = useValidator(getTemplateValidationTranslatableErrorText(t));

    const [isTemplateDataModified, setIsTemplateDataModified] = useState(false);
    const [partnerLanguageModalIsOpen, setPartnerLanguageModalIsOpen] = useState(false);
    const [defaultLanguageModalIsOpen, setDefaultLanguageModalIsOpen] = useState(false);

    const { getTemplateModels, getPartnerLanguage } = useCancelableRequests({
        getTemplateModels: TemplateHttpService.getTemplateModels,
        getPartnerLanguage: UtilsHttpService.getPartnerLanguage,
    });

    const setSubscriptionRelatedDataForDynamicVariables = (dynamicVariables: DynamicVariableChannelData[]) => {
        return dynamicVariables.map((dynamicVariable): DynamicVariableChannelData => ({
            ...dynamicVariable,
            channels: Object.keys(dynamicVariable.channels).reduce((acc, channelKey) => {
                const channel = clone(dynamicVariable.channels[channelKey]);
                channel.isDisabled = isLoadingPartnerSettings;
                channel.isBlocked = !hasSubscriptionPlaceholder(channel?.Id);
                acc[channelKey] = channel;
                return acc;
            }, {}),
        }));
    };

    const fetchDynamicVariables = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const requestPromises: Promise<any>[] = [];

        const mainRequestPromise = new Promise((resolve, reject) => {
            doGetRequest(getTemplateModels.request, {
                queryString: {
                    eventId,
                    channel: TemplateTypesLabels[templateType],
                },
                successCallback: (Data) => resolve({ type: templateType, variables: Data }),
                errorCallback: reject,
            });
        });
        requestPromises.push(mainRequestPromise);

        if (
            (templateType === TemplateTypeEnum.Viber && isAllowViberSmsCascading) ||
            (templateType === TemplateTypeEnum.WhatsAppProvider && isAllowWhatsAppSmsCascading)
        ) {
            const smsRequestPromise = new Promise((resolve, reject) => {
                doGetRequest(getTemplateModels.request, {
                    queryString: {
                        eventId,
                        channel: TemplateTypesLabels[TemplateTypeEnum.SMS],
                    },
                    successCallback: (Data) => resolve({ type: TemplateTypeEnum.SMS, variables: Data }),
                    errorCallback: reject,
                });
            });

            requestPromises.push(smsRequestPromise);
        }

        Promise.all(requestPromises).then(
            (channelsData) => {
                const groupedDynamicVariables = groupDynamicVariablesByDisplayName(channelsData);
                const dynamicVariables = setSubscriptionRelatedDataForDynamicVariables(groupedDynamicVariables);
                setTemplateDynamicVariables(dynamicVariables);
            },
            // doGetRequest has already shown the error toaster; ignore only the request rejection.
            // Errors thrown above are not caught here and still surface.
            noop,
        );
    };

    useEffect(() => {
        if (!isLoading && templateData) {
            if (withDynamicValues) {
                if (!isNil(templateDynamicVariables)) {
                    let replacedTemplateData = replaceOldPlaceholders(
                        templateData,
                        templateDynamicVariables,
                        templateType,
                    );
                    replacedTemplateData = replaceAllLanguagesTopImageUrlPlaceholders(
                        templateData,
                        templateDynamicVariables,
                        templateType,
                    );
                    dispatch(setTemplateValue(replacedTemplateData));
                    setIsTemplateDataModified(true);
                }
            } else {
                setIsTemplateDataModified(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, templateDynamicVariables]);

    const init = () => {
        if (isNil(isAllowViberSmsCascading) || isNil(isAllowWhatsAppSmsCascading)) return;

        if (withDynamicValues === true) {
            fetchDynamicVariables();
        }

        isEmpty(partnerLanguages) &&
            getPartnerLanguageList(dispatch, getPartnerLanguage.request).catch(ignoreCanceledError);
    };

    const deleteTab = (templateLangKey: string, index: number) => {
        const fieldNames = keys(values(templateData)[index]).reduce((acc, key) => {
            if (key !== 'Language') {
                acc.push(templateLangKey + key);
            }
            return acc;
        }, [] as string[]);

        removeValidationProperties(fieldNames);
        dispatch(deleteTemplateLanguage(templateLangKey));
    };

    const handleTabDelete = (templateLangKey: string, index: number) => () => {
        deleteTab(templateLangKey, index);
        if (index === +tabActiveKey) {
            setTabActiveKey('0');
        } else if (index < +tabActiveKey) {
            setTabActiveKey(`${tabActiveKey - 1}`);
        }
    };

    const handleModalIsVisible = () => {
        setPartnerLanguageModalIsOpen(!partnerLanguageModalIsOpen);
    };

    const handleDefaultLanguageModalIsVisible = () => {
        setDefaultLanguageModalIsOpen(!defaultLanguageModalIsOpen);
    };

    const onConfirmSetDefaultLanguageModal = (templateId, isDeletable) => {
        dispatch(
            setTemplateAsDefault({
                targetId: 'defaultLang',
                template: templateData[templateId],
                type: templateType,
            }),
        );

        if (isDeletable) {
            const templateIndex = keys(templateData).findIndex((item) => item === templateId);

            deleteTab(templateId, templateIndex);
        }

        setTabActiveKey('0');
        setDefaultLanguageModalIsOpen(!defaultLanguageModalIsOpen);
    };

    const handleTabActiveKey = (index) => {
        const templateDataValues = values(templateData),
            templateDataKeys = keys(templateData);
        validator(
            keys(templateDataValues[tabActiveKey]).reduce((acc, key) => {
                if (key === TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping) {
                    return acc;
                }
                if (key === TemplateInputTypes.ButtonUrl || key === TemplateInputTypes.ButtonText) {
                    acc[templateDataKeys[tabActiveKey] + key] = {
                        text: templateData[templateDataKeys[tabActiveKey]]?.ButtonText,
                        url: templateData[templateDataKeys[tabActiveKey]]?.ButtonUrl,
                    };
                } else {
                    acc[templateDataKeys[tabActiveKey] + key] = templateData[templateDataKeys[tabActiveKey]][key];
                }
                return acc;
            }, {}),
        );
        setTabActiveKey(index);
    };

    const isValidTab = (templateLangKey) => {
        let isValid = false;

        const keysToCheck = [...keys(templateData[templateLangKey]), TemplateInputTypes.ImageURLPlaceholderDropdown];
        if (templateType === TemplateTypeEnum.WhatsApp) {
            keysToCheck.push(
                TemplateInputTypes.ExternalWhatsAppTemplateDetails,
                TemplateInputTypes.ExternalWhatsAppTemplatePlaceholderMapping,
            );
        }

        keysToCheck.forEach((key) => {
            if (pageValidation[templateLangKey + key]?.isValid === false) {
                isValid = true;
            }
        });

        return isValid;
    };

    const isDeletableTemplate = (templateLangKey) => {
        return templateLangKey !== 'defaultLang' && (templateData[templateLangKey]?.isDeletable ?? true);
    };

    const getDefaultLanguageModalData = (allLanguages, tabLanguages) => {
        return tabLanguages.reduce((acc, item) => {
            acc.push({
                label: allLanguages[item].Name,
                value: item,
            });
            return acc;
        }, []);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [isAllowViberSmsCascading, isAllowWhatsAppSmsCascading]);

    useEffect(() => {
        if (!isLoading && withDynamicValues && !isNil(templateDynamicVariables) && templateData) {
            validator({
                CrossValidation: {
                    dynamicValues: templateDynamicVariables,
                    data: templateData,
                    partnerLanguages: partnerLanguages,
                    shortenerAndTrackerSettings: shortenerAndTrackerSettings,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateData, isLoading, templateDynamicVariables]);

    return (
        <div className="crm-create-template-page-content">
            {defaultLanguageModalIsOpen && (
                <SetDefaultLanguageModal
                    titleLabel={t(l.SetDefaultLanguage)}
                    data={getDefaultLanguageModalData(
                        partnerLanguages,
                        keys(templateData).filter((key) => key !== 'defaultLang'),
                    )}
                    defaultValue={keys(templateData)[tabActiveKey]}
                    onCancel={handleDefaultLanguageModalIsVisible}
                    onOk={onConfirmSetDefaultLanguageModal}
                />
            )}
            {partnerLanguageModalIsOpen && (
                <LanguageWithSearchModal
                    onCancel={handleModalIsVisible}
                    onOk={handleModalIsVisible}
                    languageList={createLanguageModalData(
                        values(partnerLanguages),
                        keys(templateData).filter((key) => key !== 'defaultLang'),
                    )}
                    templateType={templateType}
                    addValidationProperties={addValidationProperties}
                    defaultTemplate={templateData?.defaultLang}
                />
            )}
            <BusyLoader
                isBusy={!isTemplateDataModified}
                type="spinner"
                spinnerSize="big"
                className="crm-loader-template-page-content"
            >
                {!isEmpty(templateData) && isTemplateDataModified && (
                    <Tabs
                        onChange={handleTabActiveKey}
                        actions={
                            <>
                                <Button
                                    disabled={+tabActiveKey === 0}
                                    appearance="minimal"
                                    className="set-default-language-btn"
                                    onClick={handleDefaultLanguageModalIsVisible}
                                >
                                    <span>{t(l.SetDefaultLanguage)}</span>
                                </Button>
                                <button className="add-translation-btn" onClick={handleModalIsVisible}>
                                    <i className={'bc-icon-profit'} />
                                    <span>{t(l.TemplateAddTranslation)}</span>
                                </button>
                            </>
                        }
                        type={'button'}
                        activeKey={`${tabActiveKey}`}
                    >
                        {keys(templateData).map((templateLangKey, i) => {
                            const language = partnerLanguages[templateLangKey];
                            const tabName = language
                                ? language.Id.toUpperCase() + (+tabActiveKey === i ? ` - ${language.Name}` : '')
                                : t(l.Default);
                            return (
                                <Tab
                                    key={i}
                                    className={`${+tabActiveKey === i ? 'active' : ''} ${
                                        isValidTab(templateLangKey) ? 'invalid-tab' : ''
                                    }`}
                                    title={<p>{tabName}</p>}
                                    {...(isDeletableTemplate(templateLangKey) && {
                                        onClose: handleTabDelete(templateLangKey, i),
                                    })}
                                >
                                    {isLoading || (withDynamicValues && isNil(templateDynamicVariables)) ? (
                                        <BusyLoader
                                            isBusy
                                            type="spinner"
                                            spinnerSize={'big'}
                                            className="crm-full-width crm-justify-content-center"
                                        />
                                    ) : (
                                        <CommonTemplateContent
                                            key={i}
                                            index={i}
                                            templateLangKey={templateLangKey}
                                            templateData={templateData[templateLangKey]}
                                            templateType={templateType}
                                            dynamicVariables={templateDynamicVariables}
                                            maxSMSPartsCount={maxSMSPartsCount}
                                            getAssets={getAssets}
                                            addAsset={addAsset}
                                            shortenerAndTrackerSettings={shortenerAndTrackerSettings}
                                            eventId={eventId}
                                        />
                                    )}
                                </Tab>
                            );
                        })}
                    </Tabs>
                )}
            </BusyLoader>
        </div>
    );
};

CommonTemplate.propTypes = {
    isLoading: PropTypes.bool,
    templateType: PropTypes.string,
    addValidationProperties: PropTypes.func,
    removeValidationProperties: PropTypes.func,
    maxSMSPartsCount: PropTypes.number,
    getAssets: PropTypes.func,
    addAsset: PropTypes.func,
    tabActiveKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    setTabActiveKey: PropTypes.func,
    withDynamicValues: PropTypes.bool,
    eventId: PropTypes.number,
    templateInputRef: PropTypes.object,
    shortenerAndTrackerSettings: PropTypes.array,
    templateDynamicVariables: PropTypes.array,
    setTemplateDynamicVariables: PropTypes.func,
};

CommonTemplate.defaultProps = {
    isLoading: false,
    templateType: TemplateTypeEnum.SMS,
    addValidationProperties: noop,
    removeValidationProperties: noop,
    setTabActiveKey: noop,
    getAssets: noop,
    addAsset: noop,
    tabActiveKey: 0,
    withDynamicValues: true,
    eventId: 0,
    shortenerAndTrackerSettings: [],
    templateDynamicVariables: [],
    setTemplateDynamicVariables: noop,
};

export default CommonTemplate;
